import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Globe, Clock, CheckCircle, XCircle, Circle, ArrowRight } from 'lucide-react';

// 해외 계정 홈 — 심사 상태 + 셋업 체크리스트 (온보딩 단계를 한눈에)
function GlobalStatus({ user }) {
    const navigate = useNavigate();
    const status = user?.globalStatus || 'pending_review';
    const myUid = user?.companyId || user?.uid;
    const [steps, setSteps] = useState(null);

    useEffect(() => {
        if (status !== 'active' || !myUid) return;
        (async () => {
            try {
                const [c1, c2, links, orders] = await Promise.all([
                    getDocs(query(collection(db, 'connections'), where('requesterId', '==', myUid), where('status', '==', 'accepted'))),
                    getDocs(query(collection(db, 'connections'), where('receiverId', '==', myUid), where('status', '==', 'accepted'))),
                    getDocs(query(collection(db, 'agentLinks'), where('participants', 'array-contains', myUid))),
                    getDocs(query(collection(db, 'outsourceOrders'), where('fromUserId', '==', myUid))),
                ]);
                const connected = c1.size + c2.size > 0;
                const linkDocs = links.docs.map(d => d.data()).filter(l => l.purpose === 'global_outsource' && l.status !== 'disabled');
                const hasLink = linkDocs.length > 0;
                const hasActiveLink = linkDocs.some(l => l.status === 'active');
                const hasSent = orders.docs.some(d => d.data().source === 'agent_auto');
                setSteps({ connected, hasLink, hasActiveLink, hasSent });
            } catch (e) { console.error('체크리스트 로드 실패:', e); setSteps({}); }
        })();
    }, [status, myUid]);

    if (status === 'pending_review') {
        return (
            <div style={S.center}>
                <Globe size={30} color="#4F52C7" />
                <div style={{ marginTop: 26 }}><Clock size={40} color="#B45309" /></div>
                <h1 style={S.h1}>Your account is under review</h1>
                <p style={S.p}>The DentCluster team is verifying your lab. This usually takes 1–2 business days. You will get full access once approved.</p>
            </div>
        );
    }
    if (status === 'rejected') {
        return (
            <div style={S.center}>
                <Globe size={30} color="#4F52C7" />
                <div style={{ marginTop: 26 }}><XCircle size={40} color="#DC2626" /></div>
                <h1 style={S.h1}>Application not approved</h1>
                <p style={S.p}>Unfortunately we could not verify your lab. If you believe this is a mistake, contact us at dentcluster@gmail.com.</p>
            </div>
        );
    }

    // active — 셋업 체크리스트
    const items = steps ? [
        { done: true, label: 'Account approved', desc: 'Welcome to DentCluster Global!' },
        { done: steps.connected, label: 'Connect with a Korean lab', desc: 'Browse labs, chat (auto-translated), then send a partner request.', action: () => navigate('/global/labs'), cta: 'Find Labs' },
        { done: steps.hasLink, label: 'Install the Agent & create a link', desc: 'The Agent moves your case files automatically.', action: () => navigate('/global/agent'), cta: 'Get the Agent' },
        { done: steps.hasActiveLink, label: 'Link approved & folders set', desc: 'Your lab approves the link; then pick your Sending folder and Received results folder in the Agent.', action: null },
        { done: steps.hasSent, label: 'Send your first case', desc: 'Drop a case folder into the Sending folder → review the draft in My Cases → Send.', action: () => navigate('/global/orders'), cta: 'My Cases' },
    ] : null;
    const doneCount = items ? items.filter(i => i.done).length : 0;
    const next = items ? items.find(i => !i.done) : null;

    return (
        <div style={{ maxWidth: 620, margin: '40px auto', padding: 20 }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Globe size={28} color="#4F52C7" />
                <h1 style={{ ...S.h1, marginTop: 10 }}>Welcome, {user?.businessName || 'partner'}!</h1>
                {items && doneCount < items.length
                    ? <p style={S.p}>Setup progress: <b>{doneCount} / {items.length}</b>{next ? <> — next: <b>{next.label}</b></> : null}</p>
                    : <p style={S.p}>You're all set. Drop a case folder in — it ships itself. 🎉</p>}
            </div>
            {!items ? <div style={{ color: '#5A6072', textAlign: 'center' }}>Loading…</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {items.map((it, i) => (
                        <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: 12, background: '#fff',
                            border: `1px solid ${!it.done && next === it ? '#C7D2FE' : '#E4E6EE'}`,
                            borderRadius: 12, padding: '14px 18px',
                            opacity: it.done ? 0.75 : 1,
                        }}>
                            {it.done
                                ? <CheckCircle size={20} color="#0E8A74" style={{ flexShrink: 0 }} />
                                : <Circle size={20} color={next === it ? '#4F52C7' : '#CBD5E1'} style={{ flexShrink: 0 }} />}
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 14.5, fontWeight: 700, color: '#1E2230', textDecoration: it.done ? 'line-through' : 'none' }}>{i + 1}. {it.label}</div>
                                {!it.done && <div style={{ fontSize: 12.5, color: '#5A6072', marginTop: 2 }}>{it.desc}</div>}
                            </div>
                            {!it.done && it.action && (
                                <button onClick={it.action}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 8, border: 'none', background: next === it ? '#4F52C7' : '#EEF0FA', color: next === it ? '#fff' : '#4F52C7', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                                    {it.cta} <ArrowRight size={13} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const S = {
    center: { maxWidth: 560, margin: '60px auto', padding: 20, textAlign: 'center' },
    h1: { fontSize: 22, fontWeight: 800, color: '#1E2230', margin: '14px 0 10px' },
    p: { fontSize: 14.5, color: '#5A6072', lineHeight: 1.7 },
};

export default GlobalStatus;
